import Trustpilot from "../Trustpilot";
import { Theme } from "@mui/material";
import LogoLink from "@/core/components/LogoLink/LogoLink";
import { Box } from "@mui/system";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import Advantages from "@/core/components/Advantages/Advantages";
import MediaLogos from "./MediaLogos/MediaLogos";
import { Brand, HeadingLevel, Partners } from "@/core/types/types";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import Image from "next/image";
import { partnerLogoConfig } from "@/core/data";
import { TextVariant } from "@/core/components/Field/Text";
import {
  isMediaLogosEnabled,
  isTrustPilotEnabled,
  isBrandFaceEnabled,
  areTrustLogosEnabled,
} from "@/core/utils/getBrandConfiguration";
import { HumanFaceComponent } from "./components/HumanFaceSideBar";
import { ConfigContext } from "@/core/contexts/ConfigContext";
import { useContext } from "react";
import TrustLogos from "./TrustLogos/TrustLogos";

const styles = (theme: Theme) => ({
  root: {
    left: 0,
    zIndex: 1,
    minHeight: "100vh",
    height: "100%",
    width: "340px",
    padding: theme.spacing(3, 4, 2),
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    background: theme.palette.background.default,
  },
  title: {
    color: theme.palette.primary.main,
    fontSize: theme.spacing(2.5),
    margin: theme.spacing(3, 0, 2),
    fontWeight: 700,
  },
});

interface SidebarProps {
  brandName: Brand;
  position: string;
  partnerId: Partners | null;
}

const Sidebar = ({ position, brandName, partnerId }: SidebarProps) => {
  const classes: any = useCustomStyles(styles);
  const initializationConfig = useContext(ConfigContext);

  return (
    <Box sx={{ position }}>
      <Box sx={classes?.root}>
        <LogoLink brandName={brandName} />
        <div>
          <Box sx={classes?.title}>{t("common.sidebar.advantages.title")}</Box>
          <Advantages brandName={brandName}></Advantages>
        </div>

        {isMediaLogosEnabled(brandName, initializationConfig.legalArea) &&
          !partnerId && (
            <div>
              <Box sx={classes?.title}>
                {t("common.sidebar.partners.title")}
              </Box>
              <MediaLogos
                brandName={brandName}
                legalArea={initializationConfig.legalArea}
              />
            </div>
          )}

        {isBrandFaceEnabled(brandName) && !partnerId && (
          <HumanFaceComponent brandName={brandName} />
        )}

        <Box mt={3}>
          {isMediaLogosEnabled(brandName, initializationConfig.legalArea) &&
            partnerId && (
              <Box mt={1}>
                <TextVariant
                  variant={HeadingLevel.Body2}
                  text={t("common.sidebar.partners.cooperation")}
                />
                <Box mt={1}>
                  <Image
                    alt="partner-logo"
                    src={partnerLogoConfig[partnerId]?.path}
                    width={partnerLogoConfig[partnerId]?.width}
                    height={partnerLogoConfig[partnerId]?.height}
                  />
                </Box>
              </Box>
            )}

          {isTrustPilotEnabled(brandName, initializationConfig.legalArea) && (
            <Trustpilot isSidebar />
          )}
        </Box>

        {areTrustLogosEnabled(brandName, initializationConfig.legalArea) && (
          <TrustLogos brandName={brandName} />
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;
